import React, { ReactElement } from 'react'
import type { PhysicalCardLifecycleControls } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import { CreditCardListModel } from 'src/models/creditCard.models'
import type { MccGroupCatalogue } from 'src/models/mccGroup.models'
import type {
  CardManagementGeofenceCountry,
  CardManagementMccRestrictionsResponse,
  MccRestrictionRequirements,
  PhysicalCardManagementStatus,
  ReplaceCardManagementMccRestrictionsRequest,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'

import CardSettingsTile, {
  BillingContactState,
  CountryOption,
  UpdatedBillingContact,
} from './components/CardSettingsTile/CardSettingsTile'
import CardStatusLifecycleTile, {
  isCardStatusLifecycleTileVisible,
} from './components/CardStatusLifecycleTile/CardStatusLifecycleTile'
import SpendLimitTile, {
  CompanyLineSummary,
  SpendLimitSnapshot,
  SpendLimitState,
} from './components/SpendLimitTile/SpendLimitTile'
import SpendRestrictionsTile, {
  GeofenceCountrySearchState,
  GeofenceState,
  GeofenceView,
  PaymentChannelId,
  PaymentChannelsState,
  PaymentChannelView,
} from './components/SpendRestrictionsTile/SpendRestrictionsTile'
import style from './UpdatedPhysicalCardSettings.style'

export type {
  BillingContactState,
  CompanyLineSummary,
  CountryOption,
  GeofenceCountrySearchState,
  GeofenceState,
  GeofenceView,
  PaymentChannelId,
  PaymentChannelsState,
  PaymentChannelView,
  SpendLimitSnapshot,
  SpendLimitState,
  UpdatedBillingContact,
}

export type MerchantCategoryRestrictionsState = {
  catalogue: MccGroupCatalogue[]
  restrictions?: CardManagementMccRestrictionsResponse
  canView: boolean
  canManage: boolean
  manageReasonCode?: string | null
  manageRequirements?: MccRestrictionRequirements
  isLoading: boolean
  loadError?: string
  isSaving: boolean
  saveError?: string
  saveErrorCode?: string
  unknownOutcome: boolean
  onSave: (
    request: ReplaceCardManagementMccRestrictionsRequest,
  ) => Promise<void>
  onReload: () => Promise<void>
}

type UpdatedPhysicalCardSettingsProps = {
  creditCard: CreditCardListModel
  /** Localized country list, resolved by the page. */
  countryOptions: CountryOption[]
  /** `canUpdateCreditCard` — false renders the billing disclosure read-only. */
  canEditCardDetails: boolean
  merchantCategoryRestrictions: MerchantCategoryRestrictionsState
  cardManagementStatus: PhysicalCardManagementStatus | undefined
  lifecycleControls: PhysicalCardLifecycleControls
  /**
   * Admin/Owner. Increases from anyone else are routed for approval, so the
   * spend-limit form asks them to submit a request rather than apply a change.
   */
  canApplySpendLimitDirectly: boolean
  /** `PHYSICAL_CARD_TRANSACTIONS_FEED` — required to connect the feed. */
  canConnectPhysicalCardFeed: boolean
  /** EX-40641 remote-config gate for the confirm-missing-details flow. */
  confirmCardDetailsEnabled?: boolean
  /** EX-40694 remote-config gate for the recoverable-failure retry surfaces. */
  confirmCardDetailsRetryEnabled?: boolean
  /** Brand gate for the connected-capabilities disclosure. */
  showConnectedCapabilities: boolean
  billingEditDeniedReason?: string
  billingContact?: UpdatedBillingContact
  billingContactState?: BillingContactState
  onSaveBillingContact?: (values: UpdatedBillingContact) => void
  /** Fired once a billing save lands; the drawer closes, so the page confirms. */
  onBillingContactSaved?: () => void
  isSavingBillingContact?: boolean
  billingContactSaveError?: string
  /** Card Management limits. Capability-gated on the server, not by a flag. */
  useLimitsApi?: boolean
  limits?: SpendLimitSnapshot
  limitsState?: SpendLimitState
  onApplyPermanentLimit?: (permanentLimitMinorUnits: number) => void
  isApplyingLimit?: boolean
  limitSaveError?: string
  limitSaveSucceeded?: boolean
  /** Fired once a limit save lands; the drawer closes, so the page confirms. */
  onLimitSaved?: () => void
  /** `APPLY_PHYSICAL_CARD_LIMIT_CHANGE` — read and mutation gate independently. */
  canApplyLimitChange?: boolean
  companyLine?: CompanyLineSummary
  /** Card Management payment channels. Capability-gated, per channel. */
  usePaymentChannelsApi?: boolean
  paymentChannels?: PaymentChannelView[]
  paymentChannelsState?: PaymentChannelsState
  onSetPaymentChannel?: (
    channel: PaymentChannelId,
    enabled: boolean,
    expectedVersion: number,
  ) => void
  pendingPaymentChannel?: { channel: PaymentChannelId; enabled: boolean }
  paymentChannelsSaveError?: string
  failedPaymentChannel?: PaymentChannelId
  savedPaymentChannel?: PaymentChannelId
  /** Card Management geofence (EX-40629). State-driven; no client flag. */
  geofence?: GeofenceView
  geofenceState?: GeofenceState
  geofenceCountryQuery?: string
  onGeofenceCountryQueryChange?: (query: string) => void
  geofenceCountryResults?: CardManagementGeofenceCountry[]
  geofenceCountrySearchState?: GeofenceCountrySearchState
  onChangeGeofenceCountry?: (
    change: 'ADD' | 'REMOVE',
    countryCode: string,
    expectedVersion: number,
  ) => void
  pendingGeofenceCountry?: { change: 'ADD' | 'REMOVE'; countryCode: string }
  geofenceSaveError?: string
  onPhysicalCardFeedConnected?: () => void
  onPhysicalCardFeedDisconnected?: () => void
}

/**
 * Redesigned physical card Account Settings surface (EX-40354) — a bento of
 * control tiles. Props-only: the consuming page owns the flag, the
 * permission/entitlement/brand gates, data loading and every write.
 */
export default function UpdatedPhysicalCardSettings(
  props: UpdatedPhysicalCardSettingsProps,
): ReactElement {
  const {
    creditCard,
    countryOptions,
    canEditCardDetails,
    merchantCategoryRestrictions,
    cardManagementStatus,
    lifecycleControls,
    canApplySpendLimitDirectly,
    canConnectPhysicalCardFeed,
    confirmCardDetailsEnabled = true,
    confirmCardDetailsRetryEnabled = true,
    showConnectedCapabilities,
    billingEditDeniedReason,
    billingContact,
    billingContactState,
    onSaveBillingContact,
    onBillingContactSaved,
    isSavingBillingContact,
    billingContactSaveError,
    useLimitsApi,
    limits,
    limitsState,
    onApplyPermanentLimit,
    isApplyingLimit,
    limitSaveError,
    limitSaveSucceeded,
    onLimitSaved,
    canApplyLimitChange,
    companyLine,
    usePaymentChannelsApi,
    paymentChannels,
    paymentChannelsState,
    onSetPaymentChannel,
    pendingPaymentChannel,
    paymentChannelsSaveError,
    failedPaymentChannel,
    savedPaymentChannel,
    geofence,
    geofenceState,
    geofenceCountryQuery,
    onGeofenceCountryQueryChange,
    geofenceCountryResults,
    geofenceCountrySearchState,
    onChangeGeofenceCountry,
    pendingGeofenceCountry,
    geofenceSaveError,
    onPhysicalCardFeedConnected,
    onPhysicalCardFeedDisconnected,
  } = props

  // Each tile is pinned to a grid column (see the style file for what that
  // does and does not buy), which means a hidden tile can change which tile
  // reads first. DOM order below therefore has to BE the reading order, or
  // focus order stops matching what is on screen (WCAG 2.4.3). With the
  // lifecycle tile gone, spend restrictions is what rises into the top-left,
  // so it is emitted first.
  //
  // Below the 900px breakpoint the columns collapse and this order IS the
  // stack order, so the branch below also decides mobile content priority:
  // a card with no lifecycle actions reads restrictions above spend limit.
  const showLifecycle = isCardStatusLifecycleTileVisible(
    cardManagementStatus,
    lifecycleControls,
  )

  const lifecycleTile = (
    <CardStatusLifecycleTile
      key="lifecycle"
      className="UpdatedPhysicalCardSettings-colLeft"
      status={cardManagementStatus}
      lifecycleControls={lifecycleControls}
    />
  )

  const spendLimitTile = (
    <SpendLimitTile
      key="spendLimit"
      className="UpdatedPhysicalCardSettings-colRight"
      creditCard={creditCard}
      canApplyDirectly={canApplySpendLimitDirectly}
      useLimitsApi={useLimitsApi}
      limits={limits}
      limitsState={limitsState}
      onApplyPermanentLimit={onApplyPermanentLimit}
      isApplyingLimit={isApplyingLimit}
      limitSaveError={limitSaveError}
      limitSaveSucceeded={limitSaveSucceeded}
      onLimitSaved={onLimitSaved}
      canApplyLimitChange={canApplyLimitChange}
      companyLine={companyLine}
    />
  )

  const spendRestrictionsTile = (
    <SpendRestrictionsTile
      key="spendRestrictions"
      className="UpdatedPhysicalCardSettings-colLeft"
      usePaymentChannelsApi={usePaymentChannelsApi}
      paymentChannels={paymentChannels}
      paymentChannelsState={paymentChannelsState}
      onSetPaymentChannel={onSetPaymentChannel}
      pendingPaymentChannel={pendingPaymentChannel}
      paymentChannelsSaveError={paymentChannelsSaveError}
      failedPaymentChannel={failedPaymentChannel}
      savedPaymentChannel={savedPaymentChannel}
      geofence={geofence}
      geofenceState={geofenceState}
      geofenceCountryQuery={geofenceCountryQuery}
      onGeofenceCountryQueryChange={onGeofenceCountryQueryChange}
      geofenceCountryResults={geofenceCountryResults}
      geofenceCountrySearchState={geofenceCountrySearchState}
      onChangeGeofenceCountry={onChangeGeofenceCountry}
      pendingGeofenceCountry={pendingGeofenceCountry}
      geofenceSaveError={geofenceSaveError}
      merchantCategoryRestrictions={merchantCategoryRestrictions}
    />
  )

  const cardSettingsTile = (
    <CardSettingsTile
      key="cardSettings"
      className="UpdatedPhysicalCardSettings-colRight"
      creditCard={creditCard}
      countryOptions={countryOptions}
      canEditCardDetails={canEditCardDetails}
      canConnectPhysicalCardFeed={canConnectPhysicalCardFeed}
      confirmCardDetailsEnabled={confirmCardDetailsEnabled}
      confirmCardDetailsRetryEnabled={confirmCardDetailsRetryEnabled}
      showConnectedCapabilities={showConnectedCapabilities}
      billingEditDeniedReason={billingEditDeniedReason}
      billingContact={billingContact}
      billingContactState={billingContactState}
      onSaveBillingContact={onSaveBillingContact}
      onBillingContactSaved={onBillingContactSaved}
      isSavingBillingContact={isSavingBillingContact}
      billingContactSaveError={billingContactSaveError}
      onPhysicalCardFeedConnected={onPhysicalCardFeedConnected}
      onPhysicalCardFeedDisconnected={onPhysicalCardFeedDisconnected}
    />
  )

  /*
    The three-tile branch is not just the PROCESSOR_UNSUPPORTED card — it is
    the FIRST RENDER OF EVERY LOAD. `usePhysicalCardLifecycle` reports every
    action unavailable while `expectedVersion === undefined`, i.e. for the whole
    duration of the card-management status GET, and `AccountSettings` defaults
    `lifecycleControls` to the all-unavailable set besides. So the ordinary path
    is: paint three tiles with spend restrictions top-left → status lands →
    reorder to four.

    Keys make that reorder cheap — React matches the surviving tiles by key
    rather than by position, so state they hold is not remounted away. It does
    NOT make the reorder free: React still MOVES the spend-restrictions node,
    and insertBefore on an attached node is a remove-then-insert, which blurs
    anything focused inside it. A user typing in the geofence search during the
    status GET loses focus when it resolves.

    Both problems are the same missing signal — nothing here can distinguish
    "no lifecycle actions on this card" from "lifecycle not resolved yet", so
    the four-tile order cannot simply be held until it settles. Fixing that
    means plumbing a settled/loading state down from the page; see the PR
    description.
  */
  const tiles = showLifecycle
    ? [lifecycleTile, spendLimitTile, spendRestrictionsTile, cardSettingsTile]
    : [spendRestrictionsTile, spendLimitTile, cardSettingsTile]

  return (
    <div className="UpdatedPhysicalCardSettings" css={style}>
      {tiles}
    </div>
  )
}
